import ReactCodeMirror, { DecorationSet, EditorState, EditorView } from '@uiw/react-codemirror'
import { InputBaseComponentProps, TextField } from '@mui/material'
import React from 'react'
import { com } from '@eidu/web'
import ValidFieldNamesContext from '../misc/ValidFieldNamesContext'
import highlightFieldReferences from '../misc/highlightFieldReferences'
import { labelLineToString } from '../misc/LabelUtil'
import Field = com.eidu.sharedlib.entity.field.Field
import EntityLabelLine = com.eidu.sharedlib.entity.label.EntityLabelLine

const LabelFormatDisplay = React.forwardRef<HTMLInputElement, InputBaseComponentProps>(
  ({ value }: InputBaseComponentProps, _) => {
    const singleLineExtension = EditorState.transactionFilter.of((transaction) => {
      if (transaction.newDoc.lines > 1) {
        return []
      }
      return transaction
    })

    const validFieldNames = React.useContext(ValidFieldNamesContext)

    const highlightExtension = EditorView.decorations.of(
      (view: EditorView): DecorationSet => highlightFieldReferences(view, validFieldNames)
    )

    const theme = EditorView.theme({
      '&': {
        paddingTop: '1.2ex',
        paddingBottom: '1.2ex',
        paddingLeft: '0.8ex',
        paddingRight: '0.8ex',
      },
      '&.cm-editor.cm-focused': {
        outline: 'none',
      },
    })

    return (
      <ReactCodeMirror
        style={{ display: 'block', width: '100%' }}
        value={value}
        basicSetup={{
          foldGutter: false,
          lineNumbers: false,
          highlightActiveLine: false,
        }}
        indentWithTab={false}
        extensions={[theme, singleLineExtension, highlightExtension]}
        editable={false}
      />
    )
  }
)

export type LabelLineDisplayProps = {
  title: string
  label: EntityLabelLine | undefined
  availableFields: readonly Field[]
}

const LabelLineDisplay = ({ title, label, availableFields }: LabelLineDisplayProps) => {
  const validFieldNames = availableFields.map((it) => it.name)
  return (
    <ValidFieldNamesContext.Provider value={validFieldNames}>
      <TextField
        value={label ? labelLineToString(label, availableFields) : ''}
        label={title}
        InputProps={{
          inputComponent: LabelFormatDisplay,
          fullWidth: true,
        }}
        disabled
      />
    </ValidFieldNamesContext.Provider>
  )
}

export default LabelLineDisplay
